import { _ } from "../../polyglot/polyglot.js";
import { event } from "@foretagsplatsen/yaem";
import { informDialog } from "../../widgets/dialogs/dialogs.js";
import { makeMonitorRepository } from "../../repositories/repositoryBuilders.js";
import Company from "../../../models/Company.js";
import ConfigureImportDataStep from "./ConfigureImportDataStep.js";
import session from "../../session.js";
import Wizard from "../Wizard.js";
import WizardWidget from "../WizardWidget.js";

/**
 * `AbstractImporter` is the superclass for all importers using the
 * new wizard model.
 *
 * An importer offers 2 events: `onDone` which is triggered when the
 * import wizard successfully finishes and `onAbort` which is
 * triggered when the wizard is aborted.
 */
export default class AbstractImporter {
	constructor() {
		this.onDone = event();
		this.onAbort = event();
		this._wizard = this._makeWizard();
		this._wizardWidget = this._makeWizardWidget();

		this._importRepository = makeMonitorRepository({
			route: "/Shared/Import/#destinationType/#destinationId",
			antiForgeryToken: session.getAntiForgeryToken(),
		});
	}

	start(callback) {
		this._context = {
			forcedBusinessIdentifyCode: this._forcedBusinessIdentityCode,
			companyId: this._companyId,
			company: this._company,
			agency: session.getAgency(),
			isExistingCompany: !!this._forcedBusinessIdentityCode,
			isExistingLightCompany: this._isExistingLightCompany,
			canCreateLightCompany: this._canCreateLightCompany,
			chartsOfAccounts: this._chartsOfAccounts,
		};

		this._doStart({
			onSuccess: () => {
				this._wizardWidget.start({ context: this._context });
				callback();
			},
			onError: ({ errorMessage = "" }) => {
				informDialog({
					title: _("Failed to import data"),
					body: errorMessage,
					callback: () => this._wizard.aborted.trigger(),
				}).show();
			},
		});
	}

	setChartsOfAccounts(chartsOfAccounts) {
		this._chartsOfAccounts = chartsOfAccounts;
	}

	setBusinessIdentityCode(businessIdentityCode) {
		this._forcedBusinessIdentityCode = businessIdentityCode;
	}

	setIsExistingLightCompany(isExistingLightCompany) {
		this._isExistingLightCompany = isExistingLightCompany;
	}

	canCreateLightCompany(canCreateLightCompany) {
		this._canCreateLightCompany = canCreateLightCompany;
	}

	setCompanyId(companyId) {
		this._companyId = companyId;
	}

	setCompany(company) {
		this._company = company;
	}

	getRenderable() {
		return this._wizardWidget;
	}

	getAgencyId() {
		return (
			this._context.agency?.getId() ||
			// Valid in the accounting app
			this._context.company?.getAgencyId()
		);
	}

	getReferentialApiAgencyId() {
		return (
			this._context.agency?.getReferentialApiId() ||
			// Valid in the accounting app
			this._context.company?.getReferentialApiAgencyId()
		);
	}

	getCompanyId() {
		const company = this._context.company;
		if (company && !company.isNullCompany()) {
			return company.getId();
		}
		return null;
	}

	getReferentialApiCompanyId() {
		return this._context.company.getReferentialApiId();
	}

	/**
	 * Override to perform asynchronous methods before starting the wizard
	 */
	_doStart({ onSuccess }) {
		onSuccess();
	}

	_makeWizardWidget() {
		return new WizardWidget({
			wizard: this._wizard,
			supportInfo: session.getSupportInfo(),
		});
	}

	_makeWizard() {
		const wizard = new Wizard({
			onSuccess: () => this.onDone.trigger(this._context.company),
		});

		wizard.addSteps([
			...this._wizardSteps(),
			this._createConfigureImportDataStep(),
		]);

		wizard.aborted.register(() => this.onAbort.trigger());

		return wizard;
	}

	/**
	 * Override this to return an array of steps for the import
	 * wizard.
	 *
	 * The last step in the array should update
	 * `this._context.importData` upon successful completion.
	 */
	_wizardSteps() {
		throw new Error("Subclass exception");
	}

	/**
	 * Return the last wizard step. This step lets the user configure
	 * a few things about the import data (e.g., active month, CoA,
	 * currency…).
	 */
	_createConfigureImportDataStep() {
		return new ConfigureImportDataStep({
			submitCallback: this._concludeImport.bind(this),
			wantExtendedActions:
				this._wantExtendedActionsInConfigureImportDataStep(),
			wantChartOfAccountsConfiguration:
				this._wantChartOfAccountsConfigurationInConfigureImportDataStep(),
			wantsBudgetPicker: this._wantsBudgetPicker(),
			selectOnlyLatestFiscalYear: this._selectOnlyLatestFiscalYear(),
		});
	}

	_wantExtendedActionsInConfigureImportDataStep() {
		return false;
	}

	_wantChartOfAccountsConfigurationInConfigureImportDataStep() {
		return true;
	}

	_wantsBudgetPicker() {
		return false;
	}

	_selectOnlyLatestFiscalYear() {
		return false;
	}

	_getCompanyId() {
		const company = this._context.company;
		if (company.isNullCompany()) return null;
		return company.getLegacyId();
	}

	/**
	 * Send the configured import data back to the server to validate
	 * the import. User should be redirected to the company.
	 */
	_concludeImport({ onDone, step }) {
		let destinationType;
		let destinationId;

		if (session.getAgency()) {
			destinationType = "ToAgency";
			destinationId = session.getAgency().getId();
		} else {
			destinationType = "ToCompany";
			destinationId = session.getCompany().getLegacyId();
		}

		this._importRepository
			.save({
				model: this._context.configuredData,
				filter: { destinationType, destinationId },
				notify: false,
			})
			.then((data) => {
				this._context.company = Company.materialize(data);
				onDone();
			})
			.catch((error) => {
				const errorMessage =
					error && error.responseJSON && error.responseJSON.message;

				step.addSubmitError(
					errorMessage
						? _(
								"Import failed with error message: {errorMessage}",
								{
									errorMessage,
								},
							)
						: _("There was an unknown problem during import"),
				);

				onDone();
			});
	}

	/**
	 * Override this to change the loading message.
	 */
	static loadingMessage() {
		return _("Please wait…");
	}

	static matchIntegrationType(integrationType) {
		if (!this.integrationType) {
			return false;
		}

		return integrationType === this.integrationType;
	}
}

AbstractImporter.integrationType = null;
